import { isEventExpiredForUi } from 'util/isEventExpiredForUi';
import type { Event } from 'services/api';
import type { EventApproverLog } from 'services/api/models/event.model';

/** Top event-line pill kind (maps API / labels to UI). */
export const EVENT_BRIEF_REQUEST_STATUS_KIND = {
  LIVE: 'live',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
  DEFAULT: 'default',
} as const;

export type EventBriefRequestEventLineKind =
  (typeof EVENT_BRIEF_REQUEST_STATUS_KIND)[keyof typeof EVENT_BRIEF_REQUEST_STATUS_KIND];

/** Display + API-matching copy for the event status line. */
export const EVENT_BRIEF_EVENT_LABEL = {
  CANCELLED: 'Cancelled',
  EXPIRED: 'Expired',
  LIVE_SHORT: 'Live',
  LIVE_ON_APP: 'Live on App',
} as const;

export const EVENT_BRIEF_LIVE_APP_STATUSES: Event['status'][] = [
  'PUBLISHED',
  'UPDATED',
  'RESCHEDULED',
  'FINISHED',
];

export type EventBriefStatusLine = {
  text: string;
  kind: EventBriefRequestEventLineKind;
};

/** Derives the primary status line pill from `Event` (Figma: e.g. “Live on App”, “Expired”). */
export function resolveEventBriefStatusLine (event: Event): EventBriefStatusLine {
  const label = event.eventStatusLabel?.trim() ?? '';

  if (event.status === 'CANCELLED' || label === EVENT_BRIEF_EVENT_LABEL.CANCELLED) {
    return {
      text: label || EVENT_BRIEF_EVENT_LABEL.CANCELLED,
      kind: EVENT_BRIEF_REQUEST_STATUS_KIND.CANCELLED,
    };
  }

  if (isEventExpiredForUi(event)) {
    return {
      text: EVENT_BRIEF_EVENT_LABEL.EXPIRED,
      kind: EVENT_BRIEF_REQUEST_STATUS_KIND.EXPIRED,
    };
  }

  const statusIsLive = EVENT_BRIEF_LIVE_APP_STATUSES.includes(event.status);
  if (
    statusIsLive ||
    label === EVENT_BRIEF_EVENT_LABEL.LIVE_SHORT ||
    label === EVENT_BRIEF_EVENT_LABEL.LIVE_ON_APP
  ) {
    return {
      text: EVENT_BRIEF_EVENT_LABEL.LIVE_ON_APP,
      kind: EVENT_BRIEF_REQUEST_STATUS_KIND.LIVE,
    };
  }

  if (label) return { text: label, kind: EVENT_BRIEF_REQUEST_STATUS_KIND.DEFAULT };
  return { text: '', kind: EVENT_BRIEF_REQUEST_STATUS_KIND.DEFAULT };
}

/**
 * `userEventStatus` strings from the API (must match backend copy).
 * Use these instead of hardcoding literals in UI.
 */
export const EVENT_BRIEF_USER_STATUS_LABEL = {
  APPROVED_BY_YOU: 'Approved by you',
  DENIED_BY_YOU: 'Denied by you',
  NEEDS_YOUR_REVIEW: 'Needs your review',
  AWAITING_PREVIOUS_STEP: 'Awaiting previous step',
  NO_ACTION_TAKEN: 'No Action taken',
} as const;

/** Prefix for the dynamic "awaiting another group" label (e.g. "Awaiting Office of the President"). */
export const AWAITING_GROUP_LABEL_PREFIX = 'Awaiting ';

/** Prefix for the dynamic "rejected by a group" label (e.g. "Rejected by Facility"). */
export const REJECTED_BY_GROUP_LABEL_PREFIX = 'Rejected by ';

/** Generic fallback when the rejecting group cannot be identified. */
export const REJECTED_FALLBACK_LABEL = 'Rejected';

/**
 * `waitingOn` values that are NOT a real approval group (fallbacks / event-level
 * states). For these we keep the generic "Awaiting previous step" copy.
 */
const NON_GROUP_WAITING_ON = new Set<string>([
  '',
  'pending',
  'pending approval',
  'event live',
  'cancelled',
  'expired',
]);

/**
 * Builds the per-user "another group's turn" label. When the backend tells us the
 * specific approval group it's waiting on (`event.waitingOn`, e.g. a sequential
 * title like "Office of the President", or "Facility"/"Org Admin"/"Category Approver"),
 * show "Awaiting {{group}}". Otherwise fall back to the generic "Awaiting previous step".
 */
export function composeAwaitingGroupLabel (event: Event): string {
  const group = (event.waitingOn ?? '').trim();
  if (!group || NON_GROUP_WAITING_ON.has(group.toLowerCase()))
    return EVENT_BRIEF_USER_STATUS_LABEL.AWAITING_PREVIOUS_STEP;
  return `${AWAITING_GROUP_LABEL_PREFIX}${group}`;
}

/** Display name for an approver log group, matching `ApprovalChainStep` header copy. */
function approverLogGroupName (log: EventApproverLog): string {
  if (log.isOrganization) return 'Organization Admin';
  if (log.isFacility) return 'Facility';
  return (log.categoryName ?? '').trim();
}

/**
 * When an event was rejected, find the approval group that rejected it (the first
 * log group across admin / category / sequence logs containing a `rejected` action)
 * and build a "Rejected by {{group}}" label. Falls back to `event.waitingOn`, then
 * a generic "Rejected" when the group can't be identified.
 */
export function composeRejectedByGroupLabel (event: Event): string {
  const allLogs: EventApproverLog[] = [
    ...(event.adminLogs ?? []),
    ...(event.approverLogs ?? []),
    ...(event.sequenceApprovalLogs ?? []),
  ];

  const rejectingLog = allLogs.find(log =>
    log.approvers.some(a => a.action === 'rejected'),
  );

  const group = rejectingLog
    ? approverLogGroupName(rejectingLog)
    : (event.waitingOn ?? '').trim();

  if (!group || NON_GROUP_WAITING_ON.has(group.toLowerCase()))
    return REJECTED_FALLBACK_LABEL;
  return `${REJECTED_BY_GROUP_LABEL_PREFIX}${group}`;
}

/** True when the event was rejected by an approval group (current status or a rejecting log row). */
export function isEventRejectedByGroup (event: Event): boolean {
  if (event.status === 'REJECTED') return true;
  const allLogs: EventApproverLog[] = [
    ...(event.adminLogs ?? []),
    ...(event.approverLogs ?? []),
    ...(event.sequenceApprovalLogs ?? []),
  ];
  return allLogs.some(log => log.approvers.some(a => a.action === 'rejected'));
}

export type EventBriefDotPillColors = {
  bg: string;
  border: string;
  dot: string;
  color: string;
};

export type EventBriefEventLinePillStyle = EventBriefDotPillColors & { fontWeight: number };

export const EVENT_BRIEF_EVENT_LINE_PILL: Record<
  EventBriefRequestEventLineKind,
  EventBriefEventLinePillStyle
> = {
  [EVENT_BRIEF_REQUEST_STATUS_KIND.LIVE]: {
    bg: '#E7F9F1',
    border: 'none',
    dot: '#00980B',
    color: '#00980B',
    fontWeight: 400,
  },
  [EVENT_BRIEF_REQUEST_STATUS_KIND.CANCELLED]: {
    bg: '#F4F4F4',
    border: '1px solid #D4D4D4',
    dot: '#383838',
    color: '#383838',
    fontWeight: 400,
  },
  [EVENT_BRIEF_REQUEST_STATUS_KIND.EXPIRED]: {
    bg: '#F4F4F4',
    border: '1px solid #E5E5E5',
    dot: '#64748B',
    color: '#475569',
    fontWeight: 400,
  },
  [EVENT_BRIEF_REQUEST_STATUS_KIND.DEFAULT]: {
    bg: '#F8FAFC',
    border: '1px solid #E2E8F0',
    dot: '#64748B',
    color: '#334155',
    fontWeight: 400,
  },
};

export const EVENT_BRIEF_USER_STATUS_THEME: Record<string, EventBriefDotPillColors> = {
  [EVENT_BRIEF_USER_STATUS_LABEL.APPROVED_BY_YOU]: {
    bg: '#E7F9F1',
    border: '1px solid #B8E8D0',
    dot: '#00980B',
    color: '#047857',
  },
  [EVENT_BRIEF_USER_STATUS_LABEL.DENIED_BY_YOU]: {
    bg: '#FEF2F2',
    border: '1px solid #FECACA',
    dot: '#DC2626',
    color: '#B91C1C',
  },
  [EVENT_BRIEF_USER_STATUS_LABEL.NEEDS_YOUR_REVIEW]: {
    bg: '#FFFBEB',
    border: '1px solid #FDE68A',
    dot: '#D97706',
    color: '#B45309',
  },
  [EVENT_BRIEF_USER_STATUS_LABEL.AWAITING_PREVIOUS_STEP]: {
    bg: '#F8FAFC',
    border: '1px solid #E2E8F0',
    dot: '#64748B',
    color: '#475569',
  },
  [EVENT_BRIEF_USER_STATUS_LABEL.NO_ACTION_TAKEN]: {
    bg: '#F4F4F4',
    border: '1px solid #E5E5E5',
    dot: '#383838',
    color: '#383838',
  },
};

export const EVENT_BRIEF_USER_STATUS_FALLBACK_THEME: EventBriefDotPillColors = {
  bg: '#F4F4F4',
  border: '1px solid #E5E5E5',
  dot: '#383838',
  color: '#383838',
};
